import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Send } from "lucide-react";
import i18n from "../i18n/i18n";

interface MessageSectionProps {
  onComplete: () => void;
}

const MessageSection = ({ onComplete }: MessageSectionProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [displayedStep1, setDisplayedStep1] = useState("");
  const [displayedStep1Sub, setDisplayedStep1Sub] = useState("");
  const [displayedStep2, setDisplayedStep2] = useState("");
  const [displayedStep2Sub, setDisplayedStep2Sub] = useState("");
  const [displayedStep3, setDisplayedStep3] = useState("");
  const [displayedStep3Sub, setDisplayedStep3Sub] = useState("");
  const [showStep1Sub, setShowStep1Sub] = useState(false);
  const [showStep2Sub, setShowStep2Sub] = useState(false);
  const [showStep3Sub, setShowStep3Sub] = useState(false);

  const fullMessage = i18n.t('message_card');

  const step1Text = i18n.t('scene_one_title');
  const step1SubText = i18n.t('scene_one_subtitle');
  const step2Text = i18n.t('scene_two_title');
  const step2SubText = i18n.t('scene_two_subtitle');
  const step3Text = i18n.t('scene_three_title');
  const step3SubText = i18n.t('scene_three_subtitle');

  useEffect(() => {
    if (currentStep === 0) {
      if (displayedText.length < fullMessage.length) {
        const timer = setTimeout(() => {
          setDisplayedText(fullMessage.slice(0, displayedText.length + 1));
        }, 50);
        return () => clearTimeout(timer);
      } else {
        const timer = setTimeout(() => setCurrentStep(1), 2500);
        return () => clearTimeout(timer);
      }
    }
  }, [currentStep, displayedText, fullMessage]);

  // Typing animation for step 1 main text
  useEffect(() => {
    if (currentStep === 1 && !showStep1Sub) {
      if (displayedStep1.length < step1Text.length) {
        const timer = setTimeout(() => {
          setDisplayedStep1(step1Text.slice(0, displayedStep1.length + 1));
        }, 60);
        return () => clearTimeout(timer);
      } else if (displayedStep1.length === step1Text.length) {
        const timer = setTimeout(() => {
          setShowStep1Sub(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentStep, displayedStep1, showStep1Sub, step1Text]);

  // Typing animation for step 1 sub text
  useEffect(() => {
    if (showStep1Sub && displayedStep1Sub.length < step1SubText.length) {
      const timer = setTimeout(() => {
        setDisplayedStep1Sub(step1SubText.slice(0, displayedStep1Sub.length + 1));
      }, 60);
      return () => clearTimeout(timer);
    } else if (showStep1Sub && displayedStep1Sub.length === step1SubText.length) {
      const timer = setTimeout(() => setCurrentStep(2), 2500);
      return () => clearTimeout(timer);
    }
  }, [showStep1Sub, displayedStep1Sub, step1SubText]);

  // Typing animation for step 2 main text
  useEffect(() => {
    if (currentStep === 2 && !showStep2Sub) {
      if (displayedStep2.length < step2Text.length) {
        const timer = setTimeout(() => {
          setDisplayedStep2(step2Text.slice(0, displayedStep2.length + 1));
        }, 60);
        return () => clearTimeout(timer);
      } else if (displayedStep2.length === step2Text.length) {
        const timer = setTimeout(() => {
          setShowStep2Sub(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentStep, displayedStep2, showStep2Sub, step2Text]);

  // Typing animation for step 2 sub text
  useEffect(() => {
    if (showStep2Sub && displayedStep2Sub.length < step2SubText.length) {
      const timer = setTimeout(() => {
        setDisplayedStep2Sub(step2SubText.slice(0, displayedStep2Sub.length + 1));
      }, 60);
      return () => clearTimeout(timer);
    } else if (showStep2Sub && displayedStep2Sub.length === step2SubText.length) {
      const timer = setTimeout(() => setCurrentStep(3), 2500);
      return () => clearTimeout(timer);
    }
  }, [showStep2Sub, displayedStep2Sub, step2SubText]);

  // Typing animation for step 3 main text
  useEffect(() => {
    if (currentStep === 3 && !showStep3Sub) {
      if (displayedStep3.length < step3Text.length) {
        const timer = setTimeout(() => {
          setDisplayedStep3(step3Text.slice(0, displayedStep3.length + 1));
        }, 60);
        return () => clearTimeout(timer);
      } else if (displayedStep3.length === step3Text.length) {
        const timer = setTimeout(() => {
          setShowStep3Sub(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentStep, displayedStep3, showStep3Sub, step3Text]);

  // Typing animation for step 3 sub text
  useEffect(() => {
    if (showStep3Sub && displayedStep3Sub.length < step3SubText.length) {
      const timer = setTimeout(() => {
        setDisplayedStep3Sub(step3SubText.slice(0, displayedStep3Sub.length + 1));
      }, 60);
      return () => clearTimeout(timer);
    } else if (showStep3Sub && displayedStep3Sub.length === step3SubText.length) {
      const timer = setTimeout(() => {
        onComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showStep3Sub, displayedStep3Sub, step3SubText, onComplete]);

  return (
    <section className="min-h-screen flex items-center justify-center px-6 py-20">
      <div className="max-w-2xl w-full">
        {currentStep === 0 && (
          <Card className="animate-scale-in shadow-glow border-primary/20">
            <CardContent className="p-8">
              <div className="mb-6">
                <p className="text-lg leading-relaxed text-foreground font-light">
                  {displayedText}
                  <span className="animate-pulse">|</span>
                </p>
              </div>
              <Button 
                className="w-full bg-gradient-romantic hover:shadow-glow transition-all duration-300" 
                size="lg"
              >
                <Send className="mr-2 h-5 w-5" />
               {i18n.t('send_message')}
              </Button>
            </CardContent>
          </Card>
        )}

        {currentStep === 1 && (
          <div className="text-center animate-fade-in">
            <p className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              {displayedStep1}
              {displayedStep1.length < step1Text.length && <span className="animate-pulse">|</span>}
            </p>
            {showStep1Sub && (
              <p className="text-xl text-muted-foreground">
                {displayedStep1Sub}
                {displayedStep1Sub.length < step1SubText.length && <span className="animate-pulse">|</span>}
              </p>
            )}
          </div>
        )}

        {currentStep === 2 && (
          <div className="text-center animate-fade-in">
            <p className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
              {displayedStep2}
              {displayedStep2.length < step2Text.length && <span className="animate-pulse">|</span>}
            </p>
            {showStep2Sub && (
              <p className="text-xl text-muted-foreground">
                {displayedStep2Sub}
                {displayedStep2Sub.length < step2SubText.length && <span className="animate-pulse">|</span>}
              </p>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className="text-center animate-scale-in">
            <p className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-romantic bg-clip-text text-transparent">
              {displayedStep3}
              {displayedStep3.length < step3Text.length && <span className="text-foreground animate-pulse">|</span>}
            </p>
            {showStep3Sub && (
              <p className="text-xl text-muted-foreground">
                {displayedStep3Sub}
                {displayedStep3Sub.length < step3SubText.length && <span className="animate-pulse">|</span>}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default MessageSection;